import { useEffect, useState } from "react";
import PaymentService from "../../../service/payment-service";
import useAuth from "../../../hooks/useAuth";
import "./invoice-sheet-style.scss";

//發票選擇
//
//  為什麼要在跳轉綠界之前問：電子發票是法規要求，而綠界付完款不會再問一次，
//  所以載具、捐贈、統編這些資訊必須在建單當下就收齊。
//
//  選項（尤其是愛心碼）由後端提供，前端不寫死 ——
//  愛心碼填錯要等到開立時才會失敗，那時錢已經收了。
//
//  props:
//    open         是否顯示
//    amount       要付的金額，顯示在確認鈕上
//    defaultEmail 預設信箱。沒登入的購買流程要帶（例如白夜是問診填的那個）——
//                 這支原本只會抓登入使用者的信箱，訪客會看到一個空欄位
//    defaultName  預設買受人姓名，同上
//    onClose      取消
//    onConfirm    帶著發票資訊繼續結帳
//    submitting   結帳處理中（鎖住按鈕）
const InvoiceSheet = ({
  open,
  amount,
  defaultEmail = "",
  defaultName = "",
  onClose,
  onConfirm,
  submitting,
}) => {
  const { user } = useAuth();
  const [options, setOptions] = useState({ types: [], donations: [] });
  const [type, setType] = useState("cloud");
  const [carrierNumber, setCarrierNumber] = useState("");
  const [donationCode, setDonationCode] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    //預設帶信箱，使用者不用再打一次。
    //  優先用呼叫端給的（訪客購買時是問診填的那個），其次才是登入的 Google 信箱。
    setEmail((prev) => prev || defaultEmail || user?.userEmail || "");

    PaymentService.invoiceOptions()
      .then((res) => {
        const data = res.data?.data;
        if (!data) return;
        setOptions(data);
        //預設選第一個捐贈對象，選了捐贈才不會是空的
        setDonationCode((prev) => prev || data.donations?.[0]?.code || "");
      })
      .catch(() => {});
  }, [open, user, defaultEmail]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  //前端先擋一次只是為了即時回饋；真正的把關在後端（config/invoice-options.js）
  const validate = () => {
    if (!email.trim() || !email.includes("@")) return "請填寫可收取發票的電子信箱";
    if (type === "carrier" && !/^\/[0-9A-Za-z+\-.]{7}$/.test(carrierNumber.trim()))
      return "手機條碼格式不正確（例：/ABC+123）";
    if (type === "donation" && !donationCode) return "請選擇捐贈對象";
    if (type === "company") {
      if (!/^\d{8}$/.test(companyId.trim())) return "統一編號必須是 8 位數字";
      if (!companyName.trim()) return "請填寫公司抬頭";
    }
    return "";
  };

  const handleConfirm = () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    onConfirm({
      type,
      email: email.trim(),
      buyerName: user?.userName || defaultName || "",
      carrierNumber: carrierNumber.trim(),
      donationCode,
      companyId: companyId.trim(),
      companyName: companyName.trim(),
    });
  };

  const current = options.types.find((t) => t.key === type);

  return (
    <div className="iv" role="dialog" aria-modal="true" aria-label="選擇發票方式">
      <div className="iv__mask" onClick={submitting ? undefined : onClose} />

      <div className="iv__panel">
        <div className="iv__handle" aria-hidden="true" />
        <h2 className="iv__title">選擇發票方式</h2>

        <div className="iv__types" role="radiogroup" aria-label="發票類型">
          {options.types.map((t) => (
            <button
              key={t.key}
              type="button"
              role="radio"
              aria-checked={type === t.key}
              className={type === t.key ? "iv__type is-active" : "iv__type"}
              onClick={() => {
                setType(t.key);
                setError("");
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {current?.hint && <p className="iv__hint">{current.hint}</p>}

        {/* 依類型顯示對應欄位；不需要的不出現，畫面才不會一堆空格 */}
        {type === "carrier" && (
          <label className="iv__field">
            <span>手機條碼</span>
            <input
              type="text"
              value={carrierNumber}
              onChange={(e) => setCarrierNumber(e.target.value)}
              placeholder="/ABC+123"
              //手機條碼是大寫英數，關掉自動修正避免被改成小寫或塞入空白
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
            />
          </label>
        )}

        {type === "donation" && (
          <label className="iv__field">
            <span>捐贈對象</span>
            <select
              value={donationCode}
              onChange={(e) => setDonationCode(e.target.value)}
            >
              {options.donations.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {type === "company" && (
          <>
            <label className="iv__field">
              <span>統一編號</span>
              <input
                type="text"
                inputMode="numeric"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                placeholder="12345678"
                maxLength={8}
              />
            </label>
            <label className="iv__field">
              <span>公司抬頭</span>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="公司全名"
              />
            </label>
          </>
        )}

        <label className="iv__field">
          <span>發票通知信箱</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoCapitalize="none"
            autoCorrect="off"
          />
        </label>

        {error && <p className="iv__error">{error}</p>}

        <button
          type="button"
          className="iv__confirm"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? "前往付款…" : `確認並付款 NT$${amount}`}
        </button>

        <button
          type="button"
          className="iv__cancel"
          onClick={onClose}
          disabled={submitting}
        >
          取消
        </button>
      </div>
    </div>
  );
};

export default InvoiceSheet;
